import React, { useEffect, useRef } from 'react';

const N = 5200;
const BASE_ORBIT_SIZE = 1051.5;

const STOPS = [
  [255, 255, 255],
  [255, 255, 255],
  [255, 255, 255],
  [255, 255, 255],
  [255, 255, 255],
  [255, 255, 255],
  [255, 255, 255],
] as const;

function gradientRGB(t: number): [number, number, number] {
  const n = STOPS.length - 1;
  const s = Math.max(0, Math.min(1, t)) * n;
  const i = Math.min(Math.floor(s), n - 1);
  const f = s - i;
  const a = STOPS[i] ?? STOPS[0]!;
  const b = STOPS[i + 1] ?? STOPS[n]!;
  return [a[0]+(b[0]-a[0])*f, a[1]+(b[1]-a[1])*f, a[2]+(b[2]-a[2])*f];
}

type Orbit = { rx: number; ry: number; rot: number };

const ORBITS: Orbit[] = [
  { rx: 525.5, ry: 525.5, rot: 0 },
  { rx: 400.007, ry: 400.007, rot: 0 },
  { rx: 508.07, ry: 250.114, rot: 0 },
  { rx: 234.427, ry: 508.07, rot: 0 },
  { rx: 234.427, ry: 508.07, rot: Math.PI / 4 },
  { rx: 234.427, ry: 508.07, rot: (3 * Math.PI) / 4 },
];

const ORBIT_AXIS_SPIN = [
  0.00024,
  -0.00033,
  0.00052,
  -0.00041,
  0.00067,
  -0.00058,
] as const;

interface Particle {
  orbitIdx: number;
  orbit: Orbit;
  angle: number;
  speed: number;
  size: number;
  colorT: number;
  drift: number;
}

function buildParticles(): Particle[] {
  return Array.from({ length: N }, () => {
    const orbitIdx = (Math.random() * ORBITS.length) | 0;
    const orbit = ORBITS[orbitIdx] ?? ORBITS[0]!;
    const dir = Math.random() < 0.5 ? 1 : -1;
    const speedScale = 0.7 + Math.random() * 0.9;
    return {
      orbitIdx,
      orbit,
      angle: Math.random() * Math.PI * 2,
      speed: dir * (0.0011 + Math.random() * 0.0022) * speedScale,
      size: 0.4 + Math.random() * Math.random() * 1.5,
      colorT: Math.random(),
      drift: (Math.random() - 0.5) * 0.08,
    };
  });
}

export default function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particles = buildParticles();
    let width = 1;
    let height = 1;
    let cx = 0;
    let cy = 0;
    let offsetX = 0;
    let tick = 0;
    let rafId: number;

    const resize = () => {
      width = Math.max(1, window.innerWidth);
      height = Math.max(1, window.innerHeight);
      offsetX = width * 0.1;
      cx = width / 2 + offsetX;
      cy = height / 2;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    const loop = () => {
      for (let i = 0; i < N; i++) {
        const p = particles[i]!;
        p.angle += p.speed;
      }
      tick += 1;
      const sway = Math.sin(tick * 0.0012) * 0.45;

      type PP = { sx: number; sy: number; depth: number; idx: number };
      const proj = new Array<PP>(N);

      for (let i = 0; i < N; i++) {
        const p = particles[i]!;
        const { rx, ry, rot } = p.orbit;
        const orbitAxisRot = rot + tick * (ORBIT_AXIS_SPIN[p.orbitIdx] ?? ORBIT_AXIS_SPIN[0]!);
        const ca = Math.cos(p.angle), sa = Math.sin(p.angle);
        const cr = Math.cos(orbitAxisRot), sr = Math.sin(orbitAxisRot);
        const lx = rx * ca;
        const ly = ry * sa;
        const px = lx * cr - ly * sr;
        const py = lx * sr + ly * cr;
        const depth = 0.5 + 0.5 * Math.sin(p.angle * 2.1 + p.drift + sway);
        proj[i] = {
          sx: (cx + px) * dpr,
          sy: (cy + py) * dpr,
          depth,
          idx: i,
        };
      }

      proj.sort((a, b) => a.depth - b.depth);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, width * dpr, height * dpr);
      ctx.globalCompositeOperation = 'source-over';

      const unit = (Math.min(width, height) / BASE_ORBIT_SIZE) * dpr;
      const cxPx = cx * dpr;
      const cyPx = cy * dpr;
      const lineW = Math.max(0.5, 0.5 * unit);
      const dashA = Math.max(0.5, 0.5 * unit);
      const dashB = Math.max(2, 4 * unit);
      const grad = ctx.createLinearGradient(0, cyPx - (BASE_ORBIT_SIZE * 0.5) * unit, 0, cyPx + (BASE_ORBIT_SIZE * 0.5) * unit);
      grad.addColorStop(0, 'rgba(255, 120, 86, 0.05)');
      grad.addColorStop(1, 'rgba(35, 28, 84, 0.05)');
      ctx.strokeStyle = grad;
      ctx.lineWidth = lineW;
      ctx.lineCap = 'round';
      ctx.setLineDash([dashA, dashB]);

      const drawEllipse = (rx: number, ry: number, rot = 0, idx = 0) => {
        ctx.beginPath();
        const orbitAxisRot = rot + tick * (ORBIT_AXIS_SPIN[idx] ?? ORBIT_AXIS_SPIN[0]!);
        ctx.ellipse(cxPx, cyPx, rx * unit, ry * unit, orbitAxisRot, 0, Math.PI * 2);
        ctx.stroke();
      };

      drawEllipse(525.5, 525.5, 0, 0);
      drawEllipse(400.007, 400.007, 0, 1);
      drawEllipse(508.07, 250.114, 0, 2);
      drawEllipse(234.427, 508.07, 0, 3);
      drawEllipse(234.427, 508.07, Math.PI / 4, 4);
      drawEllipse(234.427, 508.07, (3 * Math.PI) / 4, 5);

      ctx.setLineDash([]);
      ctx.globalCompositeOperation = 'lighter';

      // ── Core particles ───────────────────────────────────────────
      for (let i = 0; i < N; i++) {
        const { sx, sy, depth, idx } = proj[i]!;
        const p = particles[idx]!;
        const { rx, ry, rot } = p.orbit;
        const orbitAxisRot = rot + tick * (ORBIT_AXIS_SPIN[p.orbitIdx] ?? ORBIT_AXIS_SPIN[0]!);
        const prevAngle = p.angle - p.speed * 2.8;
        const pca = Math.cos(prevAngle), psa = Math.sin(prevAngle);
        const pcr = Math.cos(orbitAxisRot), psr = Math.sin(orbitAxisRot);
        const plx = rx * pca;
        const ply = ry * psa;
        const ppx = plx * pcr - ply * psr;
        const ppy = plx * psr + ply * pcr;
        const prevX = (cx + ppx) * dpr;
        const prevY = (cy + ppy) * dpr;
        const [r, g, b] = gradientRGB(p.colorT);
        const alpha = Math.max(0.02, (0.08 + 0.75 * depth) * 0.5);
        const sz = Math.max(0.5625 * dpr, p.size * dpr * (0.5 + depth * 0.32) * 1.25);
        ctx.strokeStyle = `rgba(${r},${g},${b},${alpha * 0.3})`;
        ctx.lineWidth = Math.max(0.6 * dpr, sz * 0.55);
        ctx.beginPath();
        ctx.moveTo(prevX, prevY);
        ctx.lineTo(sx, sy);
        ctx.stroke();
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha * 0.7})`;
        ctx.beginPath();
        ctx.arc(sx, sy, sz, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="particle-canvas"
    />
  );
}
